import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from '@material-ui/styles';
import { Button, TextField, Typography, Link } from '@material-ui/core';
import { passwordUpdateAuthPost, updateUserPasswordModifiedDetailPost } from 'actions';
import { getAuthStatus } from 'utils/settings';
import ModalCustom from 'components/ModalCustom';
import LoaderCustom from 'components/LoaderCustom';
import PasswordTooltip from 'components/PasswordTooltip';
import Banner from 'components/Banner/Banner';
import PasswordField from 'components/ChangePassword/components/PasswordField/PasswordField';

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(0, 5),
    },
    fields: {
        margin: theme.spacing(1, -1, -1, -1),
        display: 'flex',
        flexWrap: 'wrap',
        '& > *': {
            flexGrow: 1,
            margin: theme.spacing(1),
        },
    },
    submitButton: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(2),
        width: '100%',
    },
    showPassword: {
        width: '100%',
        cursor: 'pointer',
    },
    passwordInfo: {
        margin: theme.spacing(0, 0, 0, 2),
    },
    passwordInfoLabel: {
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
}));

const PASSWORD_COMPLEXITY_MESSAGE =
'Password should contain 1 upper-case, 1 lower-case letter, 1 number and 1 special character';

const ALLOWED_CHARS_REGEX = /^[A-Za-z0-9!@#$%^&*()_\-+]+$/;

const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_\-+]/;

const PASSWORD_REQUIREMENTS = [
    'Must be at least 12 characters',
    'Must contain at least one number',
    'Must have upper and lower case letters',
    'Must contain at least one symbol',
];

const createValidationSchema = (email) =>
    Yup.object().shape({
        currentPassword: Yup.string().required('Required').min(12, 'Must be at least 12 characters').max(16, 'Must be at most 16 characters'),
        password: Yup.string()
            .min(12)
            .max(16)
            .required('Required')
            .test(
                'not-same-as-current',
                'New password cannot be same as current password',
                function (value) {
                    const { currentPassword } = this.parent;
                    if (!currentPassword || !value) return true;
                    return value !== currentPassword;
                }
            ),
        confirmNewPassword: Yup.string()
            .required('Required')
            .when('password', {
                is: (val) => val && val.length > 0,
                then: Yup.string().oneOf(
                    [Yup.ref('password')],
                    'New password and confirm new password need to be the same'
                ),
            })
            .matches(/[a-z]/, PASSWORD_COMPLEXITY_MESSAGE)
            .matches(/[A-Z]/, PASSWORD_COMPLEXITY_MESSAGE)
            .matches(/[0-9]/, PASSWORD_COMPLEXITY_MESSAGE)
            .matches(SPECIAL_CHAR_REGEX, PASSWORD_COMPLEXITY_MESSAGE)
            .test(
                'allowed-chars',
                'Allowed character a-z A-Z 0-9 ! @ # $ % ^ & * ( ) _ - + (No Space)',
                function () {
                    const { confirmNewPassword } = this.parent;
                    if (!confirmNewPassword) return true;
                    return ALLOWED_CHARS_REGEX.test(confirmNewPassword);
                }
            )
            .test(
                'no-username',
                'Password should not contain username',
                function () {
                    const { confirmNewPassword } = this.parent;
                    if (!email || !confirmNewPassword) return true;

                    const lowerPassword = confirmNewPassword.toLowerCase();
                    const lowerEmail = email.toLowerCase();

                    if (lowerPassword.includes(lowerEmail)) return false;

                    const username = lowerEmail.split('@')[0];
                    if (username && lowerPassword.includes(username)) return false;

                    return true;
                }
            ),
    });

const INITIAL_VALUES = {
    currentPassword: '',
    password: '',
    confirmNewPassword: '',
};

const ChangePassword = ({ isOpen, onClose }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [isFormPosted, setIsFormPosted] = useState(false);
    const authState = useSelector((state) => state.auth);
    const commonState = useSelector((state) => state.common);
    const email = authState?.email ?? '';
    const info = isFormPosted && authState?.passwordChangeStatusMessage?.message 
        ? 'Something is not right. Please check your current password and new password.'
        : '';
    const validationSchema = createValidationSchema(email);

    const handleSubmit = values => {
        dispatch(
            passwordUpdateAuthPost({
                user: authState?.loginDetails,
                oldPassword: values?.currentPassword,
                newPassword: values?.confirmNewPassword,
            })
        );
        setIsFormPosted(true);
    };

    useEffect(() => {
        if(isFormPosted && !authState?.isPasswordUpdateLoading && authState?.passwordChangeStatusMessage === 'SUCCESS'){
            dispatch(updateUserPasswordModifiedDetailPost({ userName: email }));
        }
    }, [isFormPosted, authState?.isPasswordUpdateLoading, authState.passwordChangeStatusMessage, authState.isLoggedIn]);

    useEffect(() => {
        if (
            isFormPosted
            && authState?.passwordChangeStatusMessage === 'SUCCESS' 
            && commonState?.updatePasswordModifiedDetail?.status
            && getAuthStatus() != false 
            && getAuthStatus()
        ) {
            setIsFormPosted(false);
            onClose();
            setIsSuccessModalOpen(true);
        }
    }, [authState?.passwordChangeStatusMessage, commonState?.updatePasswordModifiedDetail?.status]);

    const handleModalClose = () => {
        setIsFormPosted(false);
        onClose();
    };

    return (
        <>
            <ModalCustom
                actions={false}
                backDrop
                close={handleModalClose}
                open={isOpen}
                title="Change Password"
                width="sm"
            >
            <Formik
                initialValues={INITIAL_VALUES}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
            >
                {({ values, touched, errors, handleChange, handleBlur, handleSubmit: formikSubmit }) => (
                    <form className={classes.root} onSubmit={formikSubmit}>
                        <Typography color="inherit" variant="subtitle1">
                            For security purposes, we request that you provide your current password to continue.
                        </Typography>
                        {info && isFormPosted && <Banner text={info} isPasswordUpdateBanner />}
                        <div className={classes.fields}>
                            <PasswordField
                                id="currentPassword"
                                name="currentPassword"
                                label="Current Password"
                                showPassword={showCurrentPassword}
                                onToggle={() => setShowCurrentPassword((prev) => !prev)}
                                classes={classes}
                                value={values?.currentPassword}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors?.currentPassword && touched?.currentPassword}
                                helperText={
                                    errors?.currentPassword && touched?.currentPassword
                                        ? errors.currentPassword
                                        : null
                                }
                                className={
                                    errors?.currentPassword && touched?.currentPassword
                                        ? 'text-input error'
                                        : 'text-input'
                                }
                            />
                            <PasswordTooltip email={email}>
                                <TextField
                                    id="password"
                                    name="password"
                                    label="New Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors?.password && touched?.password}
                                    helperText={
                                        errors?.password && touched?.password ? errors.password : null
                                    }
                                    className={
                                        errors?.password && touched?.password
                                            ? 'text-input error'
                                            : 'text-input'
                                    }
                                    variant="outlined"
                                />
                            </PasswordTooltip>
                            <Link
                                color="secondary"
                                underline="always"
                                variant="subtitle2"
                                className={classes.showPassword}
                                onClick={() => setShowPassword((prev) => !prev)}
                            >
                                {showPassword ? 'Hide Password' : 'Show Password'}
                            </Link>
                            <PasswordField
                                id="confirmNewPassword"
                                name="confirmNewPassword"
                                label="Confirm New Password"
                                showPassword={showConfirmPassword}
                                onToggle={() => setShowConfirmPassword((prev) => !prev)}
                                classes={classes}
                                value={values?.confirmNewPassword}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors?.confirmNewPassword && touched?.confirmNewPassword}
                                helperText={
                                    errors?.confirmNewPassword && touched?.confirmNewPassword
                                        ? errors.confirmNewPassword
                                        : null
                                }
                                className={
                                    errors?.confirmNewPassword && touched?.confirmNewPassword
                                        ? 'text-input error'
                                        : 'text-input'
                                }
                            />
                        </div>
                        <Typography className={classes.passwordInfoLabel} variant="subtitle1">
                            Password must include:
                        </Typography>
                        <ul className={classes.passwordInfo}>
                            {PASSWORD_REQUIREMENTS.map((requirement, index) => (
                                <li key={index}>
                                    <Typography color="inherit" variant="subtitle1">
                                        {requirement}
                                    </Typography>
                                </li>
                            ))}
                        </ul>
                        <Button
                            type="submit"
                            className={classes.submitButton}
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={authState.isRegisterLoading}
                        >
                            UPDATE
                        </Button>
                    </form>
                )}
            </Formik>
            </ModalCustom>
            <LoaderCustom isLoading={authState?.isPasswordUpdateLoading || commonState?.isUpdatePasswordDetailsLoading} />
            <ModalCustom
                actions={false}
                backDrop
                close={() => setIsSuccessModalOpen(false)}
                open={isSuccessModalOpen}
                title="Current Password"
                width="sm"
            >
                <Typography color="inherit" variant="subtitle1">
                    Password was updated successfully.
                </Typography>
            </ModalCustom>
        </>
    );
};

export default ChangePassword;